import {logger} from '../utils/logger';
import {AxiosError, AxiosResponse} from 'axios';
import {LiteralKeyedObject} from '../types';
import {notifications} from '../utils/notification-service';

const log = logger('axios-error');

type HandledResponseCodes =
  | '400'
  | '401'
  | '403'
  | '404'
  | '406'
  | '500'
  | '503';

type ErrorHandlerFn = (error: AxiosError | any) => Promise<any> | void;
type ErrorHandlers = LiteralKeyedObject<HandledResponseCodes, ErrorHandlerFn>;

const handleBadRequest: ErrorHandlerFn = (response) => {
  log.info('Bad Request');
  return Promise.resolve(response);
};

const handleUnauthenticated: ErrorHandlerFn = () => {
  log.info('Unauthenticated');

  return Promise.resolve({
    data: null,
    hasErrors: true,
    errors: [
      {
        propertyName: 'Authentication',
        errorMessage: 'Your session has expired. Please log in again',
      },
    ],
  });
};

const handleUnauthorized: ErrorHandlerFn = () => {
  log.info('Unauthorized');
  notifications.error('You are not authorized to perform this action');
};

const handleNotFound: ErrorHandlerFn = (response) => {
  log.info('Resource Not Found');
  return Promise.resolve(response);
};

const handleNotAcceptable: ErrorHandlerFn = (response) => {
  log.info('Not Acceptable');
  return Promise.resolve(response);
};

const handleServerError: ErrorHandlerFn = (response) => {
  log.info('Internal Server Error');
  // Guard against non-JSON / non-error-shaped bodies (e.g. file-download endpoints), which would
  // otherwise throw here and reject the promise unhandled (leaving spinners stuck).
  const errors = response?.data?.errors;
  const message =
    Array.isArray(errors) && errors.length > 0
      ? errors.map((x) => x.errorMessage).join(' ')
      : 'Something went wrong. Please try again.';
  notifications.error(message);
  return Promise.resolve(response);
};

const handleServiceUnavailable: ErrorHandlerFn = (response) => {
  log.info('Service Unavailable');
  return Promise.resolve(response);
};

const errorHandlers: ErrorHandlers = {
  '400': handleBadRequest,
  '401': handleUnauthenticated,
  '403': handleUnauthorized,
  '404': handleNotFound,
  '406': handleNotAcceptable,
  '500': handleServerError,
  '503': handleServiceUnavailable,
};

export const handleResponseError = async (error: AxiosError) => {
  log.info('handling response error', error.request?.['_response']);

  if (error.response) {
    const response: AxiosResponse = error.response;
    const handler = errorHandlers[response.status];
    if (handler) {
      const result = await handler(error.response);
      if (result) {
        return result;
      }
    }
  }

  return Promise.reject(error);
};